import { computed, Ref } from 'vue';
import { useStore } from 'vuex';
import constants from '@/utilities/constants';
import { translate } from '@/utilities';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import { groupByOption } from '@/types/groupByOption';
import cardStatusType from '@/types/cardStatusType';
import { getCardStatus } from '@/utilities/defaultCardStatuses';
import usePriorityOptions from '@/utilities/composables/usePriorityOptions';

type StatusKey = (typeof cardStatusType)[keyof typeof cardStatusType];

export default function useGetBoardList(
  groupBy: Ref<string>,
  listId: Ref<string | number>,
  parentListId = ''
) {
  const store = useStore();

  const { embeddedBoardAttributes, embed } = useBoardEmbed() as any;

  const currentBoardId = computed(() => {
    return embed && embeddedBoardAttributes.value.boardId
      ? embeddedBoardAttributes.value.boardId
      : store.getters.currentBoardId;
  });

  const isEpicBoard = computed(() => {
    return currentBoardId.value === constants.epicsBoardId;
  });

  const getListStatus = (list: any) => {
    const status = getCardStatus(list.behavior, true) || {};
    return {
      board_id: list.board_id,
      id: list.id,
      title: list.title || '',
      icon: status.icon || '',
      color: list.color || status.color,
      total_cards: 0,
      behavior: list.behavior,
      is_watching: list.is_watching,
    };
  };

  const getListByType = {
    unassigned: () => ({
      id: `${currentBoardId.value}-unassigned`,
      title: translate('unassigned'),
      icon: 'user-x',
      color: '#475467',
      behavior: 'unassigned',
      total_cards: 0,
    }),
    notags: () => ({
      id: `${currentBoardId.value}-notags`,
      title: translate('notags'),
      icon: 'tag',
      color: '#D0D5DD',
      behavior: 'notags',
      total_cards: 0,
    }),
    noproject: () => ({
      id: `${currentBoardId.value}-noproject`,
      title: translate('noProject'),
      icon: constants.epicDefaultIcon,
      color: 'var(--iconDefault)',
      behavior: 'noproject',
      total_cards: 0,
    }),
    member: () => {
      const members = {
        ...store.getters.getInactiveTeamMembers,
        ...store.getters.getTeamMembers,
      };
      const member = members[listId.value];
      return { ...member, title: member?.display_name, total_cards: 0 };
    },
    tag: () => {
      const tag = store.getters.getTags[listId.value];
      return {
        id: tag?.id || '',
        title: tag?.name || '',
        icon: 'tag',
        color: tag?.color || '',
        behavior: 'tag',
        total_cards: 0,
      };
    },
    space: () => {
      const space = (store.getters.getTeamProjects ?? []).find((p: any) => p.id === listId.value);
      return {
        id: space?.id || '',
        title: space?.title || '',
        icon: space?.icon?.src || '',
        color: space?.icon?.color || '',
        behavior: '',
        total_cards: 0,
      };
    },
    epic: () => {
      const epicCard = store.getters.getCards[listId.value];
      return {
        id: `${constants.epicsBoardId}-${listId.value}`,
        title: epicCard?.title || '',
        icon: epicCard?.icon?.src || constants.epicDefaultIcon,
        color: epicCard?.icon?.color || 'var(--iconDefault)',
        behavior: '',
        total_cards: 0,
      };
    },
    priority: () => {
      const { priorityOptions } = usePriorityOptions();

      const priority = priorityOptions.value.find((p: any) => p.id === listId.value);
      return {
        id: priority?.id || 0,
        title: priority?.label || '',
        icon: priority?.icon || '',
        color: priority?.customIconColor || '',
        behavior: '',
        total_cards: 0,
      };
    },
    none: () => ({
      id: 'none',
      title: translate('none'),
      icon: 'x',
      color: '#D0D5DD',
      behavior: '',
      total_cards: 0,
    }),
    statusType: () => {
      const statusId = (String(listId.value) as StatusKey) ?? cardStatusType.Backlog;
      const status = getCardStatus(statusId);
      return {
        id: statusId,
        title: status.label || '',
        icon: status.icon || '',
        color: status.color || '',
        behavior: '',
        total_cards: 0,
      };
    },
  };

  const list = computed(() => {
    if (parentListId !== '') {
      return {
        id: listId.value,
        parentListId,
        title: store.getters.lists[listId.value]?.title,
        total_cards: 0,
      };
    }
    switch (groupBy.value) {
      case groupByOption.assignee:
        return listId.value === `${currentBoardId.value}-unassigned`
          ? getListByType.unassigned()
          : getListByType.member();
      case groupByOption.tag:
        return listId.value === `${currentBoardId.value}-notags`
          ? getListByType.notags()
          : getListByType.tag();
      case groupByOption.priority:
        return getListByType.priority();
      case groupByOption.none:
        return getListByType.none();
      case groupByOption.statusType:
        return getListByType.statusType();
      case groupByOption.space:
        return getListByType.space();
      case groupByOption.epic:
        return listId.value === `${currentBoardId.value}-noproject`
          ? getListByType.noproject()
          : getListByType.epic();
      default:
        return isEpicBoard.value
          ? getListStatus(store.getters.lists[listId.value] ?? {})
          : (store.getters.lists[listId.value] ?? {});
    }
  });

  return { list };
}
